import api from './api';
import { Project, ProjectStatus, ProjectSimulation, ProjectPosition, PositionStatus } from '../types/project';

export interface AssignableUser {
    id: number;
    name: string;
    role: string;
}

export interface UploadResponse {
    fileUrl: string;
}

interface PositionCost {
    id: number;
    position_id: number;
    month: string;
    cost: number;
}

interface ProjectCosts {
    total_planned: number;
    total_actual: number;
    variance: number;
    variance_percentage: number;
    positions: Array<{
        id: number;
        role: string;
        status: string;
        planned_monthly_cost: number;
        actual_costs: PositionCost[];
        total_actual_cost: number;
        variance: number;
        variance_percentage: number;
    }>;
}

class ProjectService {
    constructor() {
        // Bind methods to preserve 'this' context
        this.transformProject = this.transformProject.bind(this);
        this.transformSimulation = this.transformSimulation.bind(this);
    }

    private formatDateForBackend(dateString: string): string {
        return dateString.split('T')[0]; // Retorna apenas a parte YYYY-MM-DD
    }

    private transformProject(project: any): Project {
        return {
            id: project.id,
            name: project.name,
            description: project.description,
            status: ProjectStatus[project.status as keyof typeof ProjectStatus],
            statusLabel: project.statusLabel,
            companyId: project.company_id,
            clientId: project.client_id,
            managerId: project.manager_id,
            selectedSimulationId: project.selected_simulation_id,
            client: project.client,
            company: project.company,
            manager: project.manager,
            simulation: project.simulation ? this.transformSimulation(project.simulation) : undefined,
            simulations: project.simulations ? project.simulations.map(this.transformSimulation) : [],
            secoes: project.secoes ?? [], // Adicionado
        };
    }

    private transformSimulation(simulation: any): ProjectSimulation {
        return {
            id: simulation.id,
            name: simulation.name,
            margin: simulation.margin,
            start_date: simulation.start_date,
            end_date: simulation.end_date,
            duration_months: simulation.duration_months,
            monthly_results: simulation.monthly_results,
            cash_flow: simulation.cash_flow,
            total_revenue: simulation.total_revenue,
            total_costs: simulation.total_costs,
            total_profit: simulation.total_profit,
            profit_margin: simulation.profit_margin,
            positions: simulation.positions ? simulation.positions.map((pos: any) => ({
                ...pos,
                status: pos.status ? PositionStatus[pos.status as keyof typeof PositionStatus] : PositionStatus.PLANNED
            })) : [],
            is_selected: simulation.is_selected,
            rejection_reason: simulation.rejection_reason
        };
    }

    private transformSimulationForBackend(simulation: ProjectSimulation): any {
        return {
            ...simulation,
            positions: simulation.positions.map(pos => ({
                ...pos,
                status: pos.status.toString() // Convert enum to string
            }))
        };
    }

    async getManagerProjects(): Promise<Project[]> {
        const response = await api.get('/api/projects/manager');
        return response.data.map(this.transformProject);
    }

    async getProject(id: number): Promise<Project> {
        const response = await api.get(`/api/projects/${id}`);
        return this.transformProject(response.data);
    }

    async createProject(project: Omit<Project, 'id'>): Promise<Project> {
        const backendProject = {
            ...project,
            company_id: project.companyId,
            client_id: project.clientId,
            manager_id: project.managerId,
            selected_simulation_id: project.selectedSimulationId,
        };
        const response = await api.post('/api/projects', backendProject);
        return this.transformProject(response.data);
    }

    async updateProject(id: number, project: Project): Promise<Project> {
        const backendProject = {
            ...project,
            company_id: project.companyId,
            client_id: project.clientId,
            manager_id: project.managerId,
            selected_simulation_id: project.selectedSimulationId,
        };
        const response = await api.put(`/api/projects/${id}`, backendProject);
        return this.transformProject(response.data);
    }

    async deleteProject(id: number): Promise<void> {
        await api.delete(`/api/projects/${id}`);
    }

    async getProjectSimulation(projectId: number, simulationId: number): Promise<ProjectSimulation> {
        const response = await api.get(`/api/projects/${projectId}/simulations/${simulationId}`);
        return this.transformSimulation(response.data);
    }

    async saveProjectSimulation(projectId: number, simulation: ProjectSimulation): Promise<ProjectSimulation> {
        const backendSimulation = {
            ...this.transformSimulationForBackend(simulation),
            start_date: this.formatDateForBackend(simulation.start_date),
            end_date: this.formatDateForBackend(simulation.end_date),
            duration_months: simulation.duration_months,
            is_selected: simulation.is_selected
        };
        const response = await api.post(`/api/projects/${projectId}/simulations`, backendSimulation);
        return this.transformSimulation(response.data);
    }

    async deleteProjectSimulation(projectId: number, simulationId: number): Promise<void> {
        await api.delete(`/api/projects/${projectId}/simulations/${simulationId}`);
    }

    async selectProjectSimulation(projectId: number, simulationId: number): Promise<Project> {
        const response = await api.post(`/api/projects/${projectId}/simulations/${simulationId}/select`);
        return response.data;
    }

    async updateProjectSimulation(projectId: number, simulation: ProjectSimulation): Promise<ProjectSimulation> {
        const backendSimulation = {
            ...this.transformSimulationForBackend(simulation),
            start_date: this.formatDateForBackend(simulation.start_date),
            end_date: this.formatDateForBackend(simulation.end_date),
            duration_months: simulation.duration_months
        };
        const response = await api.put(`/api/projects/${projectId}/simulations/${simulation.id}`, backendSimulation);
        return this.transformSimulation(response.data);
    }

    async createProjectSimulation(projectId: number, simulation: ProjectSimulation): Promise<ProjectSimulation> {
        const backendSimulation = {
            ...this.transformSimulationForBackend(simulation),
            start_date: this.formatDateForBackend(simulation.start_date),
            end_date: this.formatDateForBackend(simulation.end_date),
            duration_months: simulation.duration_months
        };
        const response = await api.post(`/api/projects/${projectId}/simulations`, backendSimulation);
        return this.transformSimulation(response.data);
    }

    async rejectProjectSimulation(projectId: number, simulationId: number, rejectionReason: string): Promise<Project> {
        const response = await api.post(`/api/projects/${projectId}/simulations/${simulationId}/reject`, {
            rejection_reason: rejectionReason
        });
        return response.data;
    }

    async acceptProjectProposal(projectId: number): Promise<Project> {
        const response = await api.post(`/api/projects/${projectId}/accept`);
        return this.transformProject(response.data);
    }

    async getAvailableSections(): Promise<any[]> {
        // Rota do backend: proposal_sections.route('/sections') registrado em /api
        const response = await api.get('/api/sections');
        return response.data;
    }

    async createSharedProposal(projectId: number, data: any): Promise<any> {
        const response = await api.post(`/api/projects/${projectId}/share`, data);
        return response.data;
    }

    async getSharedProposal(token: string): Promise<any> {
        const response = await api.get(`/api/proposals/${token}`);
        return response.data;
    }

    async deactivateSharedProposal(token: string): Promise<void> {
        await api.post(`/api/proposals/${token}/deactivate`);
    }

    async uploadFile(file: FormData, onProgress: (progress: number) => void): Promise<UploadResponse> {
        const response = await api.post('/api/upload', file, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                onProgress(progress);
            }
        });
        return response.data;
    }

    async registerPositionCost(projectId: number, positionId: number, month: string, cost: number): Promise<PositionCost> {
        const response = await api.post(`/api/projects/${projectId}/positions/${positionId}/costs`, {
            month,
            cost
        });
        return response.data;
    }

    async getPositionCosts(projectId: number, positionId: number, startDate?: string, endDate?: string): Promise<PositionCost[]> {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);

        const response = await api.get(`/api/projects/${projectId}/positions/${positionId}/costs`, {
            params
        });
        return response.data;
    }

    async getAssignableUsers(projectId: number): Promise<AssignableUser[]> {
        const response = await api.get(`/api/projects/${projectId}/assignable-users`);
        return response.data;
    }

    // Preenche a posição com um colaborador e cria a alocação (habilita o lançamento de horas)
    async fillPosition(projectId: number, positionId: number, data: {
        user_id: number; start_date: string; end_date?: string | null; allocation_percentage: number;
    }): Promise<void> {
        await api.post(`/api/projects/${projectId}/positions/${positionId}/fill`, data);
    }

    async releasePosition(projectId: number, positionId: number): Promise<void> {
        await api.post(`/api/projects/${projectId}/positions/${positionId}/release`);
    }

    async getProjectCosts(projectId: number, month?: string): Promise<ProjectCosts> {
        const params = new URLSearchParams();
        if (month) params.append('month', month);

        const response = await api.get(`/api/projects/${projectId}/costs`, {
            params
        });
        return response.data;
    }

    async openPositionVacancy(projectId: number, positionId: number, data: any): Promise<void> {
        await api.post(`/api/projects/${projectId}/positions/${positionId}/open-vacancy`, data);
    }
}

export const projectService = new ProjectService();